import React from 'react';
import { MobxComponent } from "../../../mobx/components";
import { inject, observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { IconSettings, IconClose, IconNoImage } from "./icons";
import { ColorPicker, Switch } from "@lib/components";
import { HeaderTitleWrapper, MenuItem, QuickSettingsContainer, QuickSettingsHeaderWrapper, QuickSettingsBodyWrapper, QuickSettingsFooterWrapper, QuickSettingsButton, QuickSettingsItemsContainer, QuickSettingsItemsWrapper, QuickSettingsItemWrapper, ButtonV2, MenuColorHeaderWrapper, MenuColorHeaderTitleWrapper, MenuColorDescription, MenuColorBodyWrapper, MenuColorFormGroup, MenuColorLabel, MenuColorColorPickerWrapper, MenuColorColorPickerText, MenuColorPresetsList, MenuColorPresetsListItem, MenuColorPresetsListItemContent, MenuColorFooterWrapper, MenuUploadContainer, MenuUploadText, MenuUploadButtonGroup, MenuUploadButton } from "./layout.c";
import { CoreUtils } from '@lib/common';
import { ImageUpload } from '../../ui/dashboard/views/website/upload_image';
import shortid from 'shortid';
import { ImageUploadV2 } from './imageuploadv2';
import { capitalize, isObject } from 'lodash';
import { OptionalText } from '../../ui/restaurant/views/settings-v2';

export const PRESET_COLORS = [
    '#FDCF8D',
    '#FBA427',
    '#BB7003',
    '#6F4302',
    '#FFC9CA',
    '#FF9C9D',
    '#FF1C28',
    '#9F000B',
    '#C9CACC',
    '#AFAFB4',
    '#696877',
    '#1B1631',
    '#96B8F5',
    '#3A84DA',
    '#2C67AD',
    '#1F4D84',
    '#FFEA80',
    '#FFD633',
    '#CCA300',
    '#8C6F00',
    '#8DC1C0',
    '#64C7C7',
    '#3A6F6E',
    '#2E5352',
    '#C3ACED',
    '#906DDC',
    '#714CD3',
    '#5131B6',
    '#FFCAC5',
    '#FE9D98',
    '#A26662',
    '#694341',
    '#ABE59F',
    '#60CF4A',
    '#348624',
    '#225717',
    '#A7A7A7',
    '#6A6A6A',
    '#464545',
    '#000000',
    '#FF7CBD',
    '#A64D79',
    '#741B47',
    '#4C1130'
]

interface Props extends WithTranslation {
    show?: boolean;
    onHide?: () => void;
    onSave: (color: string, media_url?: string) => void;
    title?: string;
    value?: {
        color?: string;
        media_url?: string;
        title?: string;
    };
    item: string;
    /** When bulk-editing multiple items, hide image upload (color only). */
    hideItemImage?: boolean;
}
interface State {
    color: string;
    mediaObject: any;
}

@inject("store") @observer
class MenuColorClass extends MobxComponent<Props, State> {
	private colorPickerRef = React.createRef<ColorPicker>();

	private closeColorPicker = () => {
		this.colorPickerRef.current?.setActive(false);
	};

	private handleDismiss = () => {
		this.closeColorPicker();
		this.props.onHide?.();
	};

    constructor(props: Props) {
		super(props);
		this.state = {
            color: props.value?.color || '#000000',
            mediaObject: [props.value?.media_url && CoreUtils.image.ucReverse(props.value.media_url) || undefined]
        };
	}

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.show && !this.props.show) {
            this.closeColorPicker();
        }
        if(prevProps.value !== this.props.value) {
            this.setState({
                color: this.props.value?.color!,
                mediaObject: [this.props.value?.media_url && CoreUtils.image.ucReverse(this.props.value.media_url) || undefined]
            })
        }
    }

    render() {
        const { store, t} = this.injected;
        const { show, onSave, title, item, hideItemImage } = this.props;
        const { color, mediaObject } = this.state;
        return (
        <QuickSettingsContainer className={show ? 'active': ''}>
            <MenuColorHeaderWrapper>
                <MenuColorHeaderTitleWrapper>
                    {title || 'Acme Pizza'}
                </MenuColorHeaderTitleWrapper>
                <MenuItem onClick={this.handleDismiss}><IconClose /></MenuItem>
            </MenuColorHeaderWrapper>
            <MenuColorBodyWrapper style={{minHeight: '50vh', overflowY:'scroll', overflowX:'hidden'}}>
                {!hideItemImage && (
                <MenuColorFormGroup style={{ flexDirection: 'column', gap: 8 }}>
                    <MenuColorLabel>{capitalize(item || 'Menu')} Image <OptionalText>(Optional)</OptionalText></MenuColorLabel>
                    <ImageUploadV2
                    value={mediaObject}
                    onChange={(files) => {
                        this.setState({
                            mediaObject: files
                        })
                    }}
                    onRemove={() => this.setState({
                        mediaObject: [undefined]
                    })}
                /> </MenuColorFormGroup> )}
                <MenuColorDescription>Select a color to represent this item.</MenuColorDescription>
                <MenuColorFormGroup>
                    <MenuColorLabel>{capitalize(item || 'Menu')} Color</MenuColorLabel>
                    <MenuColorColorPickerWrapper>
                        <ColorPicker
                            ref={this.colorPickerRef}
                            color={color}
                            onChange={(color) => this.setState({
                                color: color
                            })}/>
                        {color && <MenuColorColorPickerText>{'#' + CoreUtils.colors.rgbToHex(color).toUpperCase()}</MenuColorColorPickerText>}
                    </MenuColorColorPickerWrapper>
                </MenuColorFormGroup>
                <MenuColorLabel>Pre-sets</MenuColorLabel>
                <MenuColorPresetsList>
                    {PRESET_COLORS.reduce((rows: string[][], color: string, index: number) => {
                    if (index % 2 === 0) {
                        rows.push([color]);
                    } else {
                        rows[rows.length - 1].push(color);
                    }
                    return rows;
                    }, []).map((pair: string[], rowIndex: number) => (
                    <MenuColorPresetsListItem key={rowIndex}>
                        {pair.map((color: string, i: number) => (
                        <MenuColorPresetsListItemContent
                            key={i}
                            style={{ backgroundColor: color, border: color === this.state.color ? '3px solid #fff': '' }}
                            onClick={() => this.setState({color})}
                        >
                            <span>{color}</span>
                        </MenuColorPresetsListItemContent>
                        ))}
                    </MenuColorPresetsListItem>
                    ))}

                </MenuColorPresetsList>
            </MenuColorBodyWrapper>
            <MenuColorFooterWrapper>
                <ButtonV2
                    variant='default'
                    onClick={this.handleDismiss}>
                    Cancel
                </ButtonV2>
                <ButtonV2
                    onClick={() => {
                        if (hideItemImage) {
                            onSave(color);
                            return;
                        }
                        //@ts-ignore
                        const convertMedia = isObject(mediaObject) ?  CoreUtils.image.uc(mediaObject[0]): mediaObject;
                        onSave(color, convertMedia)
                    }}>
                    Save
                </ButtonV2>
            </MenuColorFooterWrapper>
        </QuickSettingsContainer>
        )
    }
}
export const MenuColor = withTranslation()(MenuColorClass);
